// This is the Search Component Locked to the bottom of the screen
import { React, useState, useEffect, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import MentionEditor from "./suggestion.jsx";
import TipTapMention from "./TipTapMention.jsx";
import Cookies from "js-cookie";
import "../Styles/Search.scss";
import RecordButton from "./RecordButton.jsx";
import useAppStore from "../../Store/SearchStore.jsx";
import { useCurrentEditor } from "@tiptap/react";
import "../Styles/TipTap.scss";
import { MentionProvider } from "./MentionContext.jsx";
import RatingFaces from "../Rating/Rating.jsx";
import { encodeSearchText, decodeSearchText } from "../../Utils/urlEncoder.js";
import { extractPersonaFromURL } from "../../Utils/personaUrlUtils.js";
import { motion } from "framer-motion";
import useAuthStore from "../../Store/AuthStore";
import { useTranslation } from "react-i18next";

import Tooltip from "../ui/tooltip.jsx";

// Animation configurations
const ANIMATION_CONFIG = {
  searchPage: {
    initial: { opacity: 0, y: 100 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
  },
  homePage: {
    initial: false,
    animate: false,
  },
  exit: {
    opacity: 0,
    scale: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const TextArea = ({ pageType }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuthStore();

  // Destructuring properties from the custom hook useAppStore
  const {
    searchText,
    setSearchText,
    handleSearch,
    getWantedGifts,
    placeholders,
    placeholderIndex,
    rotatePlaceholder,
    tag,
    count,
    incrementCount,
  } = useAppStore();

  // State hooks for focus management and placeholder handling
  const [isFocused, setIsFocused] = useState(false);
  const [focusedPlaceholder, setFocusedPlaceholder] = useState("");
  const [recordingState, setRecordingState] = useState("idle");
  const history = useHistory();
  const location = useLocation();
  const [buttonContent, setButtonContent] = useState("button");
  const [userInfo, setUserInfo] = useState({});

  const editorRef = useRef(null);
  const quillRef = useRef(null);

  const EditorJSONPreview = () => {
    const { editor } = useCurrentEditor();
  };

  // This effect hook is responsible for extracting the 'query' parameter from the URL,
  // decoding it, and then using it to set the search text and perform the search.
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const query = queryParams.get("query");

    if (query) {
      try {
        const decodedQuery = decodeSearchText(query);
        const { personaId: urlPersonaId, summary: personaSummary } = extractPersonaFromURL(decodedQuery);

        // Get the currently selected persona from PersonaStore
        const PersonaStore = require("../../Store/PersonaStore.jsx").default;
        const { selectedPersona, setSelectedPersona, userPersonas, isProgrammaticSelection } = PersonaStore.getState();

        // If we're in the middle of a programmatic selection (e.g., creating persona), don't interfere
        if (isProgrammaticSelection) {
          return;
        }

        if (urlPersonaId) {
          // URL contains a persona ID
          if (selectedPersona && selectedPersona._id === urlPersonaId) {
            // URL persona matches selected persona - proceed normally
            setSearchText(decodedQuery);
            performSearch(decodedQuery);
          } else if (userPersonas.length > 0) {
            // Only validate if userPersonas is loaded
            const urlPersona = userPersonas.find(p => p._id === urlPersonaId);

            if (urlPersona) {
              // Switch to the persona from URL
              if (!selectedPersona || selectedPersona._id !== urlPersona._id) {
                setSelectedPersona(urlPersona);
                // The setSelectedPersona will trigger search automatically
              } else {
                setSearchText(decodedQuery);
                performSearch(decodedQuery);
              }
            } else {
              // Persona from URL not found - trigger persona generation
              handleMissingPersona(personaSummary, decodedQuery, setSelectedPersona, setSearchText);
            }
          } else {
            // userPersonas not loaded yet - let PersonaStore handle it
            setSearchText(decodedQuery);
            performSearch(decodedQuery);
          }
        } else {
          // No persona in URL - deselect if needed
          if (selectedPersona && userPersonas.length > 0) {
            setSelectedPersona(null);
          }
          setSearchText(decodedQuery);
          performSearch(decodedQuery);
        }
      } catch (error) {
        console.error("Error decoding search query:", error);
      }
    } else {
      // Handle unauthenticated user search text
      handleUnauthUserSearch();
    }
  }, [location, setSearchText]);

  // Helper function to handle missing persona scenario
  const handleMissingPersona = (personaSummary, decodedQuery, setSelectedPersona, setSearchText) => {
    if (personaSummary && isAuthenticated) {
      setSelectedPersona(null);
      setSearchText(personaSummary);
      localStorage.setItem("prompt", personaSummary);
      performSearch(personaSummary);
    } else {
      setSearchText(decodedQuery);
      performSearch(decodedQuery);
    }
  };

  // Helper function to handle unauthenticated user search
  const handleUnauthUserSearch = () => {
    const unauthUserSearchText = localStorage.getItem("unauthUserSearchText");
    if (unauthUserSearchText) {
      try {
        const decodedQuery = decodeSearchText(unauthUserSearchText);
        setSearchText(decodedQuery);
        performSearch(decodedQuery);
        localStorage.removeItem("unauthUserSearchText");
      } catch (error) {
        console.error("Error decoding stored search query:", error);
      }
    }
  };

  // This function performs the search operation by fetching the wanted gifts
  // and handling the search based on the provided searchText.
  const performSearch = async (searchText) => {
    const cookie = Cookies.get();
    await getWantedGifts(cookie, true); // Pass true for scroll
    if (isAuthenticated) {
      incrementCount();
    }
  };

  // Function to handle search button click, triggers two async functions
  const handleSearchButtonClick = async () => {
    if (pageType === "home") {
      history.push("/search"); // Redirect to search page
    }
    // await getWantedGifts(searchText); // Fetch gifts based on searchText
    // await handleSearch(searchText); // Perform search operation with searchText
    // performSearch();
    console.log(searchText);
    const encodedSearchText = encodeSearchText(searchText);
    history.push(`/search?query=${encodedSearchText}`); // Redirect to search page with query params
    console.log("searchbuttonclick");
  };

  // Effect hook to rotate placeholder text at an interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      rotatePlaceholder();
    }, 1500);

    return () => clearInterval(intervalId);
  }, [rotatePlaceholder]);

  // Memoize translated placeholders - updates when language changes
  const translatedPlaceholders = useMemo(
    () => placeholders.map(p => t(p)),
    [placeholders, t, i18n.language]
  );

  const currentPlaceholder = translatedPlaceholders[placeholderIndex];

  const handleFocus = () => {
    setIsFocused(true);
    setFocusedPlaceholder(currentPlaceholder);
  };

  // Function to handle blur event on textarea
  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleEditorChange = (content, delta, source, editor) => {
    setSearchText(content);
    console.log(content);
  };

  const handleAddButton = () => {
    // Logic to set button content
    setButtonContent("New Button");
  };

  // Determine additional class based on pageType
  const additionalClasses =
    pageType === "searchPage"
      ? "flex justify-center items-center min-h-fit"
      : pageType === "home"
        ? "h-40 bottom-7 px-2 py-1 w-11/12 flex flex-col justify-center rounded-3xl border-2 shadow-lg bg-stone-50 border-1 border-red-500"
        : pageType === "eventPage"
          ? "flex justify-center items-center min-h-fit "
          : "sticky top-0 z-10  bg-stone-50 w-9/11 justify-center items-center px-2.5 py-2 mb-auto mx-4 mt-9 rounded-3xl border border-solid border-red-500 shadow-md max-md:mb-auto";
  const additionalClasses2 =
    pageType === "searchPage"
      ? "px-2 py-1 w-11/12 flex flex-col justify-center rounded-3xl border-solid shadow-lg bg-stone-50 border-1 border-[color:var(--Color-Red-red-500,#AD1700)] "
      : pageType === "home"
        ? "h-full py-2 h-full pr-2.5 pt-4 w-full rounded-3xl max-md:max-w-full"
        : pageType === "eventPage"
          ? "fixed bottom-7 px-2 py-1 w-11/12 flex flex-col justify-center rounded-3xl border-solid shadow-md bg-stone-50 border-1 border-[color:var(--Color-Red-red-500,#AD1700)] "
          : "";
  const additionalClasses3 =
    pageType === "searchPage"
      ? "h-full w-full py-2 flex gap-3 pr-2.5 w-full rounded-3xl max-md:max-w-full max-w-full max-h-full  "
      : pageType === "home"
        ? "flex gap-2.5 h-full "
        : pageType === "eventPage"
          ? "h-full w-full py-2 flex gap-3 pr-2.5 w-full rounded-3xl max-md:max-w-full max-w-full max-h-full  "
          : "";
  const textAreaClass =
    pageType === "searchPage"
      ? "px-4 flex-auto my-auto text-base align-text-top font-bold leading-6 text-left bg-stone-50 text-neutral-green-500 focus:outline-none resize-none"
      : pageType === "home"
        ? "px-4 h-full flex-auto my-auto text-base align-text-top font-bold leading-6 text-left bg-stone-50 text-neutral-green-500 focus:outline-none resize-none"
        : "px-4 h-full flex-auto my-auto text-base align-text-top font-bold leading-6 text-left bg-stone-50 text-neutral-green-500 focus:outline-none resize-none";

  // Get animation configuration based on page type
  const getAnimationConfig = () => {
    const isSearchPage = pageType === "searchPage";
    return {
      layout: false,
      layoutId: undefined,
      initial: isSearchPage
        ? ANIMATION_CONFIG.searchPage.initial
        : ANIMATION_CONFIG.homePage.initial,
      animate: isSearchPage
        ? ANIMATION_CONFIG.searchPage.animate
        : ANIMATION_CONFIG.homePage.animate,
      exit: ANIMATION_CONFIG.exit,
    };
  };

  // Component JSX
  return (
    <motion.div className={additionalClasses} {...getAnimationConfig()}>
      <motion.div className={additionalClasses2} layout={false}>
        <motion.div className={additionalClasses3} layout={false}>
          <TipTapMention
            ref={editorRef}
            initialContent={searchText}
            placeholder={currentPlaceholder}
            onContentChange={setSearchText}
            searchText={searchText}
            setSearchText={setSearchText}
            handleSearchButtonClick={handleSearchButtonClick}
            focusedPlaceholder={currentPlaceholder}
            placeholders={translatedPlaceholders}
            isFocused={isFocused}
            placeholderIndex={placeholderIndex}
            rotatePlaceholder={rotatePlaceholder}
          // addButton={<button onClick={handleClearContent}>Clear Content</button>} // Pass the button content as a prop
          />
          <div className="flex gap-2.5 justify-end bottom-0 items-end">
            <RecordButton pageType={pageType} onRecordingStateChange={setRecordingState} />

            <Tooltip
              content={t("search.title")}
              placement="top"
              animation="shift-away"
              theme="green-tooltip"
            >
              <div
                className="relative group flex justify-center items-center w-10 h-10 bg-red-500 rounded-3xl aspect-square border-2 border-red-500 active:bg-red-700 focus:bg-red-700 hover:cursor-pointer px-4"
                onClick={() => {
                  handleSearchButtonClick(); // Trigger search on button click
                }}
              >
                <button>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.75 14.75L10.25 10.25M11.75 6.5C11.75 9.39949 9.39949 11.75 6.5 11.75C3.60051 11.75 1.25 9.39949 1.25 6.5C1.25 3.60051 3.60051 1.25 6.5 1.25C9.39949 1.25 11.75 3.60051 11.75 6.5Z"
                      stroke="#E6F4F4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </Tooltip>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default TextArea;